class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    document.addEventListener('click', async (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.refreshing) return;
      this.refreshing = true;
      cartLink.setAttribute('aria-busy', 'true');
      this.open(cartLink);
      try {
        await this.refresh();
        const panel = this.querySelector('.drawer__inner');
        trapFocus(panel, panel.querySelector('.drawer__close'));
      } catch {
        const error = this.querySelector('#CartDrawer-CartErrors');
        if (error) error.textContent = window.cartStrings.error;
      } finally {
        this.refreshing = false;
        cartLink.removeAttribute('aria-busy');
      }
    });
  }

  showStatus(message, error = false) {
    const panel = this.querySelector('.drawer__inner');
    let status = panel.querySelector('.vtx-cart-status');
    if (!status) {
      status = document.createElement('p');
      status.className = 'vtx-cart-status';
      panel.prepend(status);
    }
    status.setAttribute('role', error ? 'alert' : 'status');
    status.classList.toggle('vtx-cart-status--error', error);
    status.textContent = message;
  }

  async refresh() {
    const url = new URL(window.vertexaCartUrl(window.routes?.cart_url || '/cart'), location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url.href);
    if (!response.ok) throw new Error('Cart could not be loaded');
    this.updateContents(await response.json());
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.activeElement) this.setActiveElement(document.activeElement);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => {
      const panel = this.querySelector('.drawer__inner');
      trapFocus(panel, panel.querySelector('.drawer__close'));
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  updateContents(sections) {
    const html = sections?.['cart-drawer'];
    if (!html) throw new Error('Cart section missing');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('#CartDrawer');
    if (!source) throw new Error('Cart markup missing');
    this.querySelector('#CartDrawer').innerHTML = source.innerHTML;
    this.classList.toggle('is-empty', parsed.querySelector('cart-drawer')?.classList.contains('is-empty'));
    const icon = document.getElementById('cart-icon-bubble');
    if (icon && sections['cart-icon-bubble']) {
      icon.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
    }
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    try {
      this.updateContents(parsedState.sections);
    } catch {
      await this.refresh();
    }
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
